/**
 * React Query hooks for scan data fetching with automatic caching and refetching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as scansApi from '@/api/scans';
import type { ScanCreateRequest, ScanScheduleCreateRequest, ScanScheduleUpdateRequest } from '@/types/scan';

/** Fetch all scans with optional status filter. Refetches every 10 seconds. */
export function useScans(status?: string) {
  return useQuery({
    queryKey: ['scans', status],
    queryFn: () => scansApi.getScans(status),
    refetchInterval: 10_000,
  });
}

/** Fetch a single scan by ID. Refetches every 5 seconds while running. */
export function useScan(scanId: string) {
  return useQuery({
    queryKey: ['scans', scanId],
    queryFn: () => scansApi.getScan(scanId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' || status === 'pending' ? 5_000 : false;
    },
  });
}

/** Mutation to create and trigger a new scan. */
export function useCreateScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScanCreateRequest) => scansApi.createScan(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scans'] }),
  });
}

/** Mutation to cancel a scan. */
export function useCancelScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scanId: string) => scansApi.cancelScan(scanId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scans'] }),
  });
}

/** Fetch all scan schedules. */
export function useSchedules() {
  return useQuery({
    queryKey: ['scan-schedules'],
    queryFn: () => scansApi.getSchedules(),
  });
}

/** Mutation to create a scan schedule. */
export function useCreateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScanScheduleCreateRequest) => scansApi.createSchedule(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scan-schedules'] }),
  });
}

/** Mutation to update a schedule (name, cron, scan type, active state). */
export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & ScanScheduleUpdateRequest) =>
      scansApi.updateSchedule(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scan-schedules'] }),
  });
}

/** Mutation to delete a scan schedule. */
export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) => scansApi.deleteSchedule(scheduleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scan-schedules'] }),
  });
}

/** Mutation to delete a scan. */
export function useDeleteScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scanId: string) => scansApi.deleteScan(scanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scans'] });
      // Potentially invalidate individual scan queries too if needed:
      // queryClient.invalidateQueries({ queryKey: ['scans', scanId] });
    },
  });
}
